import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import { authHeader } from '../services/authHeaderService';
import { actionCreators, SUBMITTING_FORMS } from '../store/Form';

const CONTACT_LINK = '/services/data/v42.0/sobjects/Contact/';
const CREATE = 'create';

const isMarried = (maritalStatus) => {
    let marriedStatus = ['Married', 'Common-Law'];
    return marriedStatus.indexOf(maritalStatus) >= 0;
};

const NON_ACTIVE_CONTACT_STATUSES = ['Deceased', 'Inactive'];

export const isNonActiveContactStatus = (status) => {
    if (!status) {
        return false;
    }
    return NON_ACTIVE_CONTACT_STATUSES.indexOf(status) >= 0;
};

export function removeSpouseIfApplicable(Contact, dispatch, previousContactState) {
    if (isMarried(Contact.FinServ__MaritalStatus__c) === false &&
        isMarried(previousContactState.FinServ__MaritalStatus__c) === true) {
        dispatch(actionCreators.updateSpouse({}));
        dispatch(actionCreators.updateSpousalContactContactRelation({}));
    }
}

export function handleSpouseChange(setup) {
    const {
        dispatch,
        getState,
        initialData,
        spouse,
        previousContactState,
        recordType
    } = setup;
    const clonedFormdataSpouse = cloneDeep(spouse);

    if (!isEmpty(clonedFormdataSpouse) && spouse.FirstName) {
        dispatch({ type: SUBMITTING_FORMS, submitting: 'loading' });
        let updatedSpouse = {};
        const transaction = getState().transaction;
        const transactionSpouse = transaction.Spouse;
        
        for (const property in spouse) {
            if (spouse[property] === undefined) {
                spouse[property] = '';
            }
        }
        
        merge(updatedSpouse, transactionSpouse, spouse);

        const stateSpouseCloneWithSpouseMawerClient = cloneDeep(transactionSpouse);
        const updatedSpouseDeepClone = cloneDeep(updatedSpouse);
        const transactionContact = transaction.Contact;

        // Only submit Spouse change when:
        // 1. Spouse was actually changed (UpdatedSpouse same with state.Spouse)
        // 2. Client is not just landing on the page (!isEmpty(state.Spouse))
        // Include contact contact relationship change here too when marital status changes
        if (!isEqual(updatedSpouseDeepClone, stateSpouseCloneWithSpouseMawerClient) ||
            (transactionContact.FinServ__MaritalStatus__c !== previousContactState.FinServ__MaritalStatus__c)) {
            // const FinServ__ReciprocalRole__c = initialData.FinServ__ReciprocalRole__c;
            let SpousalContactContactRelation = getState().transaction.SpousalContactContactRelation;
            const spouseSfCompositeRequest = {
                'allOrNone': true,
                'compositeRequest': []
            };
            let spousalContactRequest = {};
            let spousalContactContactRelationRequest = {};

            // 4 scenarios:
            // 1. Married to married (Only update client marital status, make request at the end with only Contact)
            // 2. Married to not married (Only update client marital status, make request at the end with only Contact, 
            // change spouse and spousal relationship store to empty for landing page to act as no spouse any more)
            // 3. not married to married (add relation, add spouse contact info, making request right away)
            // 4. not married to not married (Behavior like item 1)
            // Overall: update client contact record accordingly since we want to keep data consistency even if only Spousal info is changed
            const spouseClone = cloneDeep(spouse);
            let spouseAccountClone = cloneDeep(spouse.Account);

            if (!spouseAccountClone) {
                spouseAccountClone = { Annual_Income__c: 0 };
            }

            delete spouseClone.Account;
            let action;
            let shouldUpdateSpouseAccount = false;
            let spousalContactId = spouseClone.Id;

            if (isMarried(transactionContact.FinServ__MaritalStatus__c) === true &&
                isMarried(previousContactState.FinServ__MaritalStatus__c) === true) {
                // Updating all spouse information, now the firstname and last name fields are locked down
                if (!isEqual(updatedSpouseDeepClone, stateSpouseCloneWithSpouseMawerClient)) {
                    delete spouseClone.Id;

                    spousalContactRequest = {
                        'method': 'PATCH',
                        'url': CONTACT_LINK + spousalContactId,
                        'referenceId': 'SpouseContact',
                        'body': spouseClone
                    };
                    spouseSfCompositeRequest.compositeRequest.push(spousalContactRequest);
                    shouldUpdateSpouseAccount = true;
                }
            } else if (isMarried(transactionContact.FinServ__MaritalStatus__c) === true &&
                isMarried(previousContactState.FinServ__MaritalStatus__c) === false) {
                action = CREATE;

                spousalContactRequest = {
                    'method': 'POST',
                    'url': CONTACT_LINK,
                    'referenceId': 'SpouseContact',
                    'body': {
                        'OwnerId': initialData.Account.properties.OwnerId,
                        'RecordTypeId': recordType.find(a => a.Name === 'Individual' && a.SobjectType === 'Contact').Id,
                        'FinServ__MaritalStatus__c': transactionContact.FinServ__MaritalStatus__c,
                        ...spouseClone
                    }
                };
                spouseSfCompositeRequest.compositeRequest.push(spousalContactRequest);

                spousalContactContactRelationRequest = {
                    'method': 'POST',
                    'url': '/services/data/v42.0/sobjects/FinServ__ContactContactRelation__c',
                    'referenceId': 'SpousalContactContactRelation',
                    'body': {
                        'FinServ__Contact__c': transactionContact.Id,
                        'FinServ__RelatedContact__c': '@{SpouseContact.id}',
                        'FinServ__Role__c': 'a0Gf4000000RYS0EAO',
                        'OwnerId': initialData.Account.properties.OwnerId
                    }
                };
                spouseSfCompositeRequest.compositeRequest.push(spousalContactContactRelationRequest);
                shouldUpdateSpouseAccount = true;
            }

            if (shouldUpdateSpouseAccount) {
                if (!spousalContactId) {
                    spousalContactId = '@{SpouseContact.id}';
                }

                const spousalContactGetRequest = {
                    'method': 'GET',
                    'url': CONTACT_LINK + spousalContactId,
                    'referenceId': 'SpouseContactForAccount'
                };
                spouseSfCompositeRequest.compositeRequest.push(spousalContactGetRequest);

                const spousalAccountRequest = {
                    'method': 'GET',
                    'url': '/services/data/v42.0/sobjects/Account/@{SpouseContactForAccount.AccountId}',
                    'referenceId': 'SpouseAccount'
                };
                spouseSfCompositeRequest.compositeRequest.push(spousalAccountRequest);

                const spousalAccountUpdateRequest = {
                    'method': 'PATCH',
                    'url': '/services/data/v42.0/sobjects/Account/@{SpouseAccount.Id}',
                    'referenceId': 'SpouseAccountUpdate',
                    'body': { 'Annual_Income__c': spouseAccountClone.Annual_Income__c }
                };
                spouseSfCompositeRequest.compositeRequest.push(spousalAccountUpdateRequest);
            }

            if (spouseSfCompositeRequest.compositeRequest.length > 0) {
                const tpiurl = `api/t/${initialData.sfId}`;
                fetch(tpiurl, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': authHeader().Authorization
                    },
                    method: 'POST',
                    body: JSON.stringify(JSON.stringify(spouseSfCompositeRequest))
                }).then(response => {
                    response.json().then(json => {
                        for (const compositeResponseElement of json.compositeResponse) {
                            if (compositeResponseElement.referenceId &&
                                compositeResponseElement.referenceId === 'SpouseContact') {
                                if (action === CREATE) {
                                    updatedSpouse.Id = compositeResponseElement.body.id;
                                }
                                dispatch(actionCreators.updateSpouse(updatedSpouse));
                            }

                            if (compositeResponseElement.referenceId &&
                                compositeResponseElement.referenceId === 'SpousalContactContactRelation' &&
                                action === CREATE) {
                                if (isNil(SpousalContactContactRelation)) {
                                    SpousalContactContactRelation = {};
                                }
                                SpousalContactContactRelation.Id = compositeResponseElement.body.id;
                                dispatch(actionCreators.updateSpousalContactContactRelation(SpousalContactContactRelation));
                            }
                        }
                        dispatch(actionCreators.updateSpouse(updatedSpouse));
                        dispatch({ type: SUBMITTING_FORMS, submitting: 'initiated' });
                    });
                });
            } else {
                dispatch(actionCreators.updateSpouse(updatedSpouse));
                dispatch({ type: SUBMITTING_FORMS, submitting: 'initiated' });
            }
        } else {
            dispatch(actionCreators.updateSpouse(updatedSpouse));
            dispatch({ type: SUBMITTING_FORMS, submitting: 'initiated' });
        }
    } else {
        dispatch(actionCreators.updateSpouse(spouse));
    }
}

export async function updateSpouse(setup) {
    const {
        contact,
        spouse,
        account,
        transactionId,
        initialData,
        recordType
    } = setup;
    const spouseSfCompositeRequest = {
        'allOrNone': true,
        'compositeRequest': []
    };

    const spouseId = spouse.Id;
    delete spouse.Id;

    delete spouse.Account;
    
    const spouseContactRequest = {
        'method': 'PATCH',
        'url': CONTACT_LINK + spouseId,
        'referenceId': 'SpouseContact',
        'body': spouse
    };
    spouseSfCompositeRequest.compositeRequest.push(spouseContactRequest);

    if (account.Spouse_Provide_Instructions__c
        && isMarried(contact.FinServ__MaritalStatus__c)
        && (initialData.Account.properties.Spouse_Provide_Instructions__c === false)) {
        const portfolios = initialData.properties.Portfolios;

        for (let i = 0; i < portfolios.length; i++) {
            const financialAccount = portfolios[i];
            const spouseFinancialAccountRoleRequest = {
                'method': 'POST',
                'url': '/services/data/v42.0/sobjects/FinServ__FinancialAccountRole__c',
                'referenceId': 'SpouseFinancialAccountRole_' + i.toString(),
                'body': {
                    'RecordTypeId': recordType.find(a => a.Name === 'Contact Role' && a.SobjectType === 'FinServ__FinancialAccountRole__c').Id,
                    'FinServ__RelatedContact__c': spouseId,
                    'FinServ__FinancialAccount__c': financialAccount.Id,
                    'FinServ__Role__c': 'Reporting Contact',
                    'Has_Web_Access__c': true,
                    'Receives_Statement_Copy__c': true
                }
            };
            spouseSfCompositeRequest.compositeRequest.push(spouseFinancialAccountRoleRequest);
        }
    }

    if (spouseSfCompositeRequest.compositeRequest.length > 0) {
        const tpiurl = `api/t${transactionId !== undefined ? `/${transactionId}` : ''}`;
        await fetch(tpiurl, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader().Authorization
            },
            method: 'POST',
            body: JSON.stringify(JSON.stringify(spouseSfCompositeRequest))
        });
    }
} 